import { Link, NavLink, Outlet } from "react-router-dom";
import { Plus, WifiOff } from "lucide-react";
import { navFor } from "@/components/layout/navItems";
import { Brand, TierPill } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/hooks/useBrowser";

function BottomNav({ items }) {
  return (
    <nav className="glass fixed inset-x-0 bottom-0 z-40 border-x-0 border-b-0 pb-safe lg:hidden" data-testid="bottom-nav">
      <ul className="mx-auto grid h-[var(--nav-h)] max-w-md" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => (
          <li key={item.key}>
            <NavLink
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex h-full flex-col items-center justify-center gap-1 text-[11px] transition-colors duration-200 ${isActive ? "text-gold" : "text-faint hover:text-ivory"}`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-5 w-5" strokeWidth={isActive ? 2 : 1.6} />
                  <span>{item.label}</span>
                  <span className={`h-0.5 w-5 rounded-full bg-gold transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Sidebar({ tier, items }) {
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-line bg-surface/50 p-6 lg:flex" data-testid="sidebar-nav">
      <Brand />
      <ul className="mt-12 space-y-1">
        {items.map((item) => (
          <li key={item.key}>
            <NavLink
              to={item.to}
              end={item.to === "/"}
              data-testid={`sidebar-nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors duration-200 ${isActive ? "bg-gold/10 text-gold" : "text-mist hover:bg-elevated hover:text-ivory"}`
              }
            >
              <item.icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="mt-auto space-y-5">
        <Link to="/book" className="btn-gold w-full" data-testid="sidebar-book-ride-btn">
          <Plus className="h-4 w-4" /> Book a Ride
        </Link>
        <TierPill tier={tier} testId="sidebar-membership-tier" />
      </div>
    </aside>
  );
}

function TopBar({ tier }) {
  return (
    <header className="glass sticky top-0 z-30 border-x-0 border-t-0 lg:hidden" data-testid="top-bar">
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
        <Link to="/" data-testid="top-bar-home-link">
          <Brand />
        </Link>
        <TierPill tier={tier} testId="top-bar-membership-tier" />
      </div>
    </header>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const online = useOnline();
  const items = navFor(user);
  return (
    <div className="min-h-screen bg-ink text-ivory">
      <Sidebar tier={user.membership_tier} items={items} />
      <div className="lg:pl-64">
        <TopBar tier={user.membership_tier} />
        {!online && (
          <div className="flex items-center justify-center gap-2 bg-elevated py-2 text-xs text-mist" data-testid="offline-banner">
            <WifiOff className="h-3.5 w-3.5 text-gold" /> You're offline — showing what we have saved.
          </div>
        )}
        <main className="mx-auto max-w-5xl px-5 pb-[calc(var(--nav-h)+2.5rem)] pt-6 sm:px-8 lg:pb-14 lg:pt-12">
          <Outlet />
        </main>
      </div>
      <BottomNav items={items} />
    </div>
  );
}
